import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

import { MfaStatus } from 'src/app/features/settings/models/mfa.models';
import { MfaService } from 'src/app/features/settings/services/mfa.service';
import {
  MfaEnrolmentPromptComponent,
  MfaEnrolmentPromptData,
} from '../mfa-enrolment-prompt/mfa-enrolment-prompt.component';

/**
 * Offers enrolment right after signing in, to people who owe a factor and
 * have none.
 *
 * Nothing is remembered across logins, deliberately: skipping lasts as long
 * as the session it was skipped in, and the next sign-in asks again. A
 * persisted dismissal would quietly defeat the grace window it exists to
 * cover.
 */
@Injectable({ providedIn: 'root' })
export class MfaEnrolmentPromptService {
  /**
   * One prompt at a time, counting from the status read rather than from the
   * dialog.
   *
   * Set before the request, not in `show()`: two logins in flight would both
   * pass a check made after an async read, and stack two dialogs. Cleared on
   * every path that does not end with a dialog, or the next login is never
   * asked.
   */
  private busy = false;

  constructor(
    private readonly mfa: MfaService,
    private readonly dialog: MatDialog
  ) {}

  /** Reads the status and prompts when one is owed. Never throws, and never
   *  blocks: a status read that fails means no prompt, not a session held
   *  outside the app. */
  maybePrompt(): void {
    if (this.busy) {
      return;
    }
    this.busy = true;

    this.mfa
      .status()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const status = response.object ?? null;
          if (status && MfaEnrolmentPromptService.owesEnrolment(status)) {
            this.show(status);
            return;
          }
          this.busy = false;
        },
        error: () => (this.busy = false),
      });
  }

  /**
   * Whether *status* describes someone who should be asked to enrol.
   *
   * `policy: 'off'` asks nobody -- that is the feature switched off -- and a
   * user whose role demands a factor is asked whatever the tenant policy
   * says.
   */
  private static owesEnrolment(status: MfaStatus): boolean {
    if (status.mfa_enabled) {
      return false;
    }
    return status.policy !== 'off' || status.required;
  }

  private show(status: MfaStatus): void {
    const data: MfaEnrolmentPromptData = { status };
    try {
      this.dialog
        .open(MfaEnrolmentPromptComponent, {
          data,
          // Required and out of grace has no way out but enrolling or signing
          // out; anyone else can dismiss it the usual ways.
          disableClose: status.required && !status.in_grace_period,
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => (this.busy = false));
    } catch {
      // A dialog that never opened must not block the next login's prompt.
      this.busy = false;
    }
  }
}
